import React, { useState, useEffect } from 'react';
import Navbar from '../Components/Navbar';
import PageHeader from '../Components/PageHeader';
import Footer from '../Components/Footer';

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const categories = [
    { id: 'all', label: 'All Posts' },
    { id: 'technology', label: 'Technology' },
    { id: 'webdev', label: 'Web Development' },
    { id: 'cloud', label: 'Cloud Computing' },
    { id: 'security', label: 'Cybersecurity' },
    { id: 'mobile', label: 'Mobile Development' },
    { id: 'tips', label: 'Tips & Tutorials' }
  ];

  const blogPosts = [
    {
      id: 1,
      title: "The Future of Cloud Computing in 2024",
      category: "cloud",
      excerpt: "Explore the latest trends and innovations shaping the cloud computing landscape in 2024, including AI integration and edge computing.",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=60",
      author: "Mohammed Abdul Maqsood",
      date: "March 15, 2024",
      readTime: "7 min read",
      tags: ["Microservices", "Architecture", "Scalability", "DevOps"]
    },
    {
      id: 8,
      title: "Database Optimization Techniques for High Traffic Websites",
      category: "tips",
      excerpt: "Learn advanced database optimization strategies to handle high traffic loads and improve application performance.",
      image: "https://images.unsplash.com/photo-1551288049-b1f3c15d4526?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=60",
      author: "Mohammed Ahmed",
      date: "February 25, 2024",
      readTime: "6 min read",
      tags: ["Database", "Performance", "Optimization", "SQL"]
    },
    {
      id: 9,
      title: "The Rise of Low-Code/No-Code Development Platforms",
      category: "technology",
      excerpt: "Exploring the growing trend of low-code and no-code platforms and their impact on traditional software development.",
      image: "https://images.unsplash.com/photo-1516321310762-7d8736b3f640?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=60",
      author: "Umar Mansuri",
      date: "February 22, 2024",
      readTime: "5 min read",
      tags: ["Low-Code", "No-Code", "Development", "Productivity"]
    }
  ];

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => setIsLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  const filteredPosts = blogPosts.filter(post => 
    (selectedCategory === 'all' || post.category === selectedCategory) &&
    (post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
     post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
     post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
  );

  const featuredPost = blogPosts[0];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <PageHeader 
        title="Blog & Insights" 
        subtitle="Stay updated with the latest technology trends, industry insights, and expert tips from our team." 
      />
      
      {/* Featured Post */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Featured Article</h2>
          </div>
          
          {isLoading ? (
            <div className="animate-pulse bg-gray-200 rounded-xl h-96" />
          ) : (
            <div className="bg-white rounded-xl overflow-hidden shadow-lg">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="relative">
                  <img 
                    src={featuredPost.image} 
                    alt={featuredPost.title}
                    className="w-full h-64 lg:h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm capitalize">
                      Featured
                    </span>
                  </div>
                </div>
                
                <div className="p-6 sm:p-8 lg:p-12 flex flex-col justify-center">
                  <div className="flex flex-wrap items-center mb-4 text-sm text-gray-600 gap-2">
                    <span className="capitalize bg-blue-100 text-blue-600 px-2 py-1 rounded">
                      {featuredPost.category}
                    </span>
                    <span>{featuredPost.date}</span>
                    <span className="mx-2 hidden sm:inline">•</span>
                    <span>{featuredPost.readTime}</span>
                  </div>
                  
                  <h3 className="text-2xl sm:text-3xl font-bold mb-4 text-gray-900">
                    {featuredPost.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-6 text-base sm:text-lg">
                    {featuredPost.excerpt}
                  </p>
                  
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold mr-3">
                        {featuredPost.author.split(' ').map(n => n[0]).join('')}
                      </div>
                      <span className="text-gray-700">{featuredPost.author}</span>
                    </div>
                    
                    <a 
                      href="#" 
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                      aria-label={`Read more about ${featuredPost.title}`}
                    >
                      Read More
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Search and Category Filter */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <input
              type="text"
              placeholder="Search posts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full max-w-md mx-auto block px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
              aria-label="Search blog posts"
            />
          </div>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 sm:px-6 sm:py-3 rounded-lg font-semibold transition-colors text-sm sm:text-base ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-200'
                }`}
                aria-pressed={selectedCategory === category.id}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse bg-gray-200 rounded-xl h-80" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.length === 0 ? (
                <p className="col-span-full text-center text-gray-600">No posts found matching your criteria.</p>
              ) : (
                filteredPosts.map((post) => (
                  <article 
                    key={post.id} 
                    className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300"
                  >
                    <div className="relative">
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-full h-48 object-cover"
                        loading="lazy"
                      />
                      <div className="absolute top-4 right-4">
                        <span className="bg-white text-blue-600 px-3 py-1 rounded-full text-sm capitalize">
                          {post.category}
                        </span>
                      </div>
                    </div>
                    
                    <div className="p-6">
                      <div className="flex items-center mb-3 text-sm text-gray-600 gap-2">
                        <span>{post.date}</span>
                        <span className="mx-2 hidden sm:inline">•</span>
                        <span>{post.readTime}</span>
                      </div>
                      
                      <h3 className="text-xl font-semibold mb-3 text-gray-900 line-clamp-2">
                        {post.title}
                      </h3>
                      
                      <p className="text-gray-600 mb-4 line-clamp-3 text-sm sm:text-base">
                        {post.excerpt}
                      </p>
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags.slice(0, 3).map((tag, index) => (
                          <span key={index} className="bg-blue-100 text-blue-600 px-2 py-1 rounded text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                      
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-semibold mr-2">
                            {post.author.split(' ').map(n => n[0]).join('')}
                          </div>
                          <span className="text-gray-700 text-sm">{post.author}</span>
                        </div>
                        
                        <a 
                          href="#" 
                          className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                          aria-label={`Read more about ${post.title}`}
                        >
                          Read More →
                        </a>
                      </div>
                    </div>
                  </article>
                ))
              )}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Subscription */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">Stay Updated</h2>
          <p className="text-lg sm:text-xl mb-10 max-w-3xl mx-auto opacity-90">
            Subscribe to our newsletter and get the latest tech insights, tutorials, and industry news delivered to your inbox.
          </p>
          
          <div className="max-w-md mx-auto">
            <div className="flex gap-4">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-300"
                aria-label="Email for newsletter subscription"
              />
              <button 
                className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
                aria-label="Subscribe to newsletter"
              >
                Subscribe
              </button>
            </div>
            <p className="text-sm opacity-75 mt-3">
              No spam, unsubscribe at any time.
            </p>
          </div>
        </div>
      </section>

      {/* Popular Topics */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Popular Topics</h2>
            <p className="text-lg sm:text-xl text-gray-600">Explore our most popular content categories</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: "💻", title: "Web Development", count: "25 Articles", color: "bg-blue-500" },
              { icon: "☁️", title: "Cloud Computing", count: "18 Articles", color: "bg-purple-500" },
              { icon: "🔒", title: "Cybersecurity", count: "15 Articles", color: "bg-red-500" },
              { icon: "📱", title: "Mobile Development", count: "20 Articles", color: "bg-green-500" },
              { icon: "🤖", title: "AI & Technology", count: "12 Articles", color: "bg-orange-500" },
              { icon: "💡", title: "Tips & Tutorials", count: "30 Articles", color: "bg-yellow-500" }
            ].map((topic, index) => (
              <div 
                key={index} 
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow cursor-pointer"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && alert(`Clicked ${topic.title}`)}
              >
                <div className={`w-12 h-12 ${topic.color} rounded-lg flex items-center justify-center text-white text-2xl mb-4`}>
                  {topic.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900">{topic.title}</h3>
                <p className="text-gray-600">{topic.count}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;